import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Constants from '../../../common/constants/Constants';
import DeploymentStateEnum from '../../../common/constants/DeploymentStateEnum';
import Loading from '../../../common/components/Loading/Loading';
import CommandJobDetails from './CommandJobDetails';
import { Lens as CircleIcon, GetApp as DownloadIcon } from '@mui/icons-material';
import { getCommandJobDetails, getReadOnlyBlobSasToken } from './CommandJobActions';
import Store from '../../../redux/Store';
import { showSnackbar } from '../../../common/state/snackbar/Snackbar.actions';
import PageContainer from '../../../common/components/Page/PageContainer';
import PermissionsEnum from '../../../common/constants/PermissionsEnum';
import PermissionsCheck from '../../../common/components/PermissionsCheck/PermissionsCheck';
import { IconButton, Tooltip, Zoom } from '@mui/material';
import { withRouter } from '../../../common/utils/NavigationWrapper';

function CommandJobDetailsContainer(props) {
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [selectedJobName, setSelectedJobName] = useState('');

  const [columnConfigs, setColumnConfigs] = useState([
    {
      name: 'Serial number',
      key: 'SerialNumber',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Type',
      key: 'Type',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Region',
      key: 'RegionName',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Group',
      key: 'GroupName',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Location',
      key: 'Location',
      type: 'string',
      visible: false,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Software version',
      key: 'SoftwareVersion',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Date and time of last command job status',
      key: 'LastUpdateJobDateTime',
      type: 'datetime',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Command job status',
      key: 'CommandJobStatus',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Error message',
      key: 'ErrorMessage',
      type: 'string',
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Connection alert',
      key: 'ConnectionAlert.Level',
      type: 'markup',
      markup: (el, col, elIndex, colIndex) => {
        var style = {
          width: '.75rem',
          height: '.75rem',
          marginTop: '5px',
          color: el.ConnectionAlert ? el.ConnectionAlert.Color : Constants.COLORS.NEUTRAL,
        };
        return <CircleIcon key={1} style={style} fill={style.color} />;
      },
      visible: true,
      filterable: true,
      sortable: true,
    },
    {
      name: 'Actions',
      key: 'Actions',
      type: 'markup',
      markup: (el, col, elIndex, colIndex) => {
        return (
          <div>
            <Tooltip disableInteractive TransitionComponent={Zoom} title="Download result">
              <IconButton
                size="large"
                key={1}
                style={{}}
                onClick={() => {
                  return _onClickDownload(el);
                }}
                data-qa="qa-download"
                disabled={el.UpdateJobStatus != DeploymentStateEnum.EXECUTION_COMPLETED}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
      visible: true,
      filterable: false,
      sortable: false,
    },
  ]);

  useEffect(() => {
    let data = null;
    const { params } = props;

    getCommandJobDetails(params.id)
      .then((res) => {
        data = res.body;
        data.forEach(
          (n) => (n.CommandJobStatus = DeploymentStateEnum.GetStateName(n.UpdateJobStatus))
        );
        setData(data);
        setIsFetching(false);
        setSelectedJobName(params.id);
      })
      .catch((err) => setIsFetching(false));
  }, []);

  useEffect(() => {
    if (props && props.packageDeploymentMessage) {
      _handlePackageDeploymentMessage(props.packageDeploymentMessage);
    }
  }, [props.packageDeploymentMessage]);

  useEffect(() => {
    if (props && props.transferProgressMessage) {
      _handleTransferProgressMessage(props.transferProgressMessage);
    }
  }, [props.transferProgressMessage]);

  const _handlePackageDeploymentMessage = (message) => {
    const { CommandGuid, DeploymentState, ErrorMessage, DeploymentStateTime } = JSON.parse(
      message.data
    );
    let newData = Object.assign([], data);
    const elIndex = newData.findIndex((el) => el.MessageCommandGuid === CommandGuid);
    if (elIndex === -1) return;
    newData[elIndex] = {
      ...newData[elIndex],
      CommandJobStatus: DeploymentStateEnum.GetStateName(DeploymentState),
      UpdateJobStatus: DeploymentState,
      ErrorMessage: ErrorMessage,
      LastCommandJobDateTime: DeploymentStateTime,
    };
    setData(newData);
  };

  const _handleTransferProgressMessage = (message) => {
    const { CommandGuid, FileSize, BytesTransferred, Status } = JSON.parse(message.data);
    let newData = Object.assign([], data);
    let elIndex = newData.findIndex((el) => el.MessageCommandGuid === CommandGuid);
    if (elIndex === -1) return;
    const percentage = Math.round((BytesTransferred / FileSize) * 100);
    const labeledStatus = Status + ' ' + percentage + '%';

    newData[elIndex] = {
      ...newData[elIndex],
      CommandJobStatus: labeledStatus,
    };
    setData(newData);
  };

  const _onClickDownload = (row) => {
    getReadOnlyBlobSasToken(row.SerialNumber, row.MessageCommandGuid)
      .then((res) => {
        let blobUrlWithToken = res.body.storageUrlWithBlobSasToken;
        var downloadWindow = window.open(blobUrlWithToken);
        try {
          downloadWindow.focus();
        } catch (e) {
          Store.dispatch(
            showSnackbar({
              type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
              message:
                'Download blocked by browser. Add this site to your exception list and try again.',
            })
          );
        }
      })
      .catch((err) => {
        Store.dispatch(
          showSnackbar({
            type: Constants.SNACKBAR_MESSAGE_TYPES.ERROR,
            message: 'Failed to download the selected file.',
          })
        );
      });
  };

  return (
    <PageContainer pageName="command-job-details">
      <PermissionsCheck appliedToPage={true} permission={PermissionsEnum.VIEW_UPDATE_MANAGEMENT}>
        <Loading condition={isFetching}>
          <div>
            <CommandJobDetails
              data={data}
              columnConfigs={columnConfigs}
              commandJobName={selectedJobName}
            />
          </div>
        </Loading>
      </PermissionsCheck>
    </PageContainer>
  );
}

const mapStateToProps = (state) => ({
  packageDeploymentMessage:
    state.push.pushMessages != null
      ? state.push.pushMessages.find((el) => el.type === 'PackageDeploymentFeedback')
      : null,
  transferProgressMessage:
    state.push.pushMessages != null
      ? state.push.pushMessages.find((el) => el.type === 'FileTransferProgress')
      : null,
});

export default withRouter(connect(mapStateToProps)(CommandJobDetailsContainer));
